import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { AxiosInstance } from '../axios.config';
import { api } from '../constants';
import { IProduct, IProductCategory, IProductDetail, IVariantApiResponse } from './types';
import { AxiosResponse } from 'axios';
import { IVariant } from '@/pages/ManageProduct/ActiveProducts';
import { ProductCount } from '../OrderManagement/types';

export interface IGetProductParams {
  status?: string;
  page?: number;
  start_date?: string;
  end_date?: string;
  search?: string;
  query: string;
}
export interface IGetOrderParams {
  status?: string;
  page?: number;
  start_date?: string;
  page_size?: number;
  end_date?: string;
  q?: string;
}

type ProductCategoryWithoutChild = Omit<IProductCategory, 'specific_child'>;
type ProductSubCategoryWithoutChild = Omit<IProductCategory, 'specific_child'>;

const getCategoriesForFilter = (): Promise<AxiosResponse<ProductCategoryWithoutChild[]>> => {
  return AxiosInstance.get(api.manageProduct.filterCategories);
};

const useGetCategoriesForFilter = (
  options?: UseQueryOptions<ProductCategoryWithoutChild[]>,
): UseQueryResult<ProductCategoryWithoutChild[] | undefined> => {
  return useQuery({
    queryKey: ['categoriesForFilter'],
    queryFn: async () => {
      const response = await getCategoriesForFilter();
      return response.data;
    },
    ...options,
  });
};

const getSubCategoriesForFilter = (
  id: number,
): Promise<AxiosResponse<ProductSubCategoryWithoutChild[]>> => {
  return AxiosInstance.get(api.manageProduct.filterSubCategories.replace(':id', id.toString()));
};

const useGetSubCategoriesForFilter = (
  id?: number, // ← change to optional / undefined allowed
  options?: UseQueryOptions<ProductSubCategoryWithoutChild[]>,
): UseQueryResult<ProductSubCategoryWithoutChild[] | undefined> => {
  return useQuery({
    queryKey: ['subCategoriesForFilter', id],
    queryFn: async () => {
      if (!id) throw new Error('Category ID is required');
      const response = await getSubCategoriesForFilter(id);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

// services/manageProduct/index.ts (or wherever useGetProducts lives)

const getProducts = async (
  params: IGetProductParams & { is_add_on?: boolean },
): Promise<{ results: IProduct[]; pageSize: number; count: number; page_count: number }> => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([value]) => value != null && value !== ''),
  );

  const response = await AxiosInstance.get(api.manageProduct.list, {
    params: filteredParams,
  });

  return response.data;
};

const useGetProducts = (
  params: IGetProductParams & { is_add_on?: boolean },
): UseQueryResult<{
  results: IProduct[];
  pageSize: number;
  count: number;
  page_count: number;
}> => {
  return useQuery({
    queryKey: ['Products', params], // ← pass params as-is
    queryFn: () => getProducts(params),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
  });
};

export const useGetProductCount = (
  filters: {
    start_date?: string;
    end_date?: string;
    category?: string | number;
    subcategory?: string | number;
  } = {},
) => {
  return useQuery<ProductCount, Error>({
    queryKey: ['productCount', filters],
    queryFn: async () => {
      const params = {
        is_add_on: false,
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.category && { category: filters.category }),
        ...(filters.subcategory && { subcategory: filters.subcategory }),
      };

      const { data } = await AxiosInstance.get(api.manageProduct.addonCount, { params });
      return data;
    },
    staleTime: 60_000,
  });
};

const deleteProduct = async (id: number): Promise<AxiosResponse> => {
  return AxiosInstance.delete(api.manageProduct.delete.replace(':id', id.toString()));
};

const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['deleteProduct'],
    mutationFn: (id: number) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Products'] });
    },
  });
};

const getProductById = async (id: string): Promise<IProductDetail> => {
  const response = await AxiosInstance.get(api.manageProduct.product_detail.replace(':id', id));
  return response.data;
};

const useGetProductDetail = (id: string): UseQueryResult<IProductDetail> => {
  return useQuery({
    queryKey: ['product_detail', id],
    queryFn: () => getProductById(id),
    enabled: !!(id && id !== '0'),
  });
};

const RestockVariant = (params: { product_id: string; data: IVariant[] | undefined }) => {
  function transformDataToVariants(products: IVariant[] | undefined): {
    variants:
      | {
          id: number;
          quantity: number;
        }[]
      | undefined;
  } {
    return {
      variants: products?.map((product) => ({
        id: product?.id,
        quantity: +product?.stock_quantity,
      })),
    };
  }

  const payload = transformDataToVariants(params.data);

  return AxiosInstance.patch(api.manageProduct.reStock.replace(':id', params.product_id), payload);
};

const useRestockVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['restockVariant'],
    mutationFn: (params: { product_id: string; data: IVariant[] | undefined }) =>
      RestockVariant(params),
    onSuccess: (_variables, params) => {
      const productId = Number(params.product_id);

      queryClient.invalidateQueries({ queryKey: ['Products'] });

      queryClient.invalidateQueries({ queryKey: ['variants', productId] });

      queryClient.refetchQueries({ queryKey: ['variants', productId] });
    },
    onError: (error) => {
      console.error('Restock failed:', error);
    },
  });
};

const getVariants = async (productId: number): Promise<IVariantApiResponse> => {
  const response = await AxiosInstance.get(
    api.manageProduct.variants.replace(':product_id', productId.toString()),
  );
  return response.data;
};

const useGetVariants = (
  productId: number | null,
  options?: UseQueryOptions<IVariantApiResponse>,
): UseQueryResult<IVariantApiResponse> => {
  return useQuery({
    queryKey: ['variants', productId],
    queryFn: () => getVariants(productId!),
    enabled: !!productId,
    ...options,
  });
};

export {
  useGetProducts,
  useGetCategoriesForFilter,
  useGetSubCategoriesForFilter,
  useDeleteProduct,
  useGetProductDetail,
  useRestockVariant,
  useGetVariants,
};
